import { useState, useCallback } from 'react';
import { blobticZoomService, ZoomData, ZoomResult } from '../services/blobticZoomService';

interface UseBlobticZoomReturn {
  openZoom: (zoomKey: string, dynamicParams?: Record<string, string>) => void;
  closeZoom: () => void;
  isOpen: boolean;
  currentZoomKey: string | null;
  currentDynamicParams: Record<string, string>;
  executeZoom: (zoomKey: string, dynamicParams?: Record<string, string>) => Promise<ZoomResult>;
  validateZoom: (zoomKey: string, dynamicParams?: Record<string, string>) => string[];
  getZoomFields: (zoomKey: string) => { fields: string[], labels: string[] };
}

/**
 * Hook personnalisé pour gérer les zooms BLOBTIC
 * Simplifie l'utilisation du système de zoom dans les composants
 */
export const useBlobticZoom = (): UseBlobticZoomReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentZoomKey, setCurrentZoomKey] = useState<string | null>(null);
  const [currentDynamicParams, setCurrentDynamicParams] = useState<Record<string, string>>({});

  /**
   * Ouvre un zoom avec les paramètres spécifiés
   */
  const openZoom = useCallback((zoomKey: string, dynamicParams: Record<string, string> = {}) => {
    setCurrentZoomKey(zoomKey);
    setCurrentDynamicParams(dynamicParams);
    setIsOpen(true);
  }, []);

  /**
   * Ferme le zoom actuel
   */
  const closeZoom = useCallback(() => {
    setIsOpen(false);
    setCurrentZoomKey(null);
    setCurrentDynamicParams({});
  }, []);

  /**
   * Exécute une action de zoom directement (sans popup)
   */
  const executeZoom = useCallback(async (
    zoomKey: string, 
    dynamicParams: Record<string, string> = {}
  ): Promise<ZoomResult> => {
    return await blobticZoomService.executeZoomAction(zoomKey, dynamicParams);
  }, []);

  /**
   * Valide un zoom avant exécution
   */
  const validateZoom = useCallback((
    zoomKey: string, 
    dynamicParams: Record<string, string> = {}
  ): string[] => {
    return blobticZoomService.validateZoom(zoomKey, dynamicParams);
  }, []);

  /**
   * Récupère les champs et labels d'un zoom
   */
  const getZoomFields = useCallback((zoomKey: string): { fields: string[], labels: string[] } => {
    return blobticZoomService.parseZoomFields(zoomKey);
  }, []);

  return {
    openZoom,
    closeZoom,
    isOpen,
    currentZoomKey,
    currentDynamicParams,
    executeZoom,
    validateZoom,
    getZoomFields
  };
};

/**
 * Hook spécialisé pour les champs de saisie avec zoom
 */
export const useZoomField = (
  zoomKey: string,
  onSelect?: (selectedData: ZoomData) => void
) => {
  const [selectedData, setSelectedData] = useState<ZoomData | null>(null);
  const [displayValue, setDisplayValue] = useState<string>('');
  const [fieldConfig, setFieldConfig] = useState<{
    fields: string[];
    labels: string[];
    displayField?: string;
    valueField?: string;
  }>({ fields: [], labels: [] });

  // Initialiser la configuration du champ
  const initializeField = useCallback((displayField?: string, valueField?: string) => {
    const { fields, labels } = blobticZoomService.parseZoomFields(zoomKey);
    setFieldConfig({
      fields,
      labels,
      displayField: displayField || fields[1] || fields[0], // Par défaut, le deuxième champ ou le premier
      valueField: valueField || fields[0] // Par défaut, le premier champ
    });
  }, [zoomKey]);

  // Gérer la sélection d'une donnée
  const handleSelect = useCallback((data: ZoomData) => {
    setSelectedData(data);
    
    // Mettre à jour la valeur d'affichage
    if (fieldConfig.displayField && data[fieldConfig.displayField] !== undefined) {
      setDisplayValue(String(data[fieldConfig.displayField]));
    }
    
    // Appeler le callback parent
    if (onSelect) {
      onSelect(data);
    }
  }, [fieldConfig.displayField, onSelect]);

  // Obtenir la valeur sélectionnée
  const getValue = useCallback(() => {
    if (!selectedData || !fieldConfig.valueField) return '';
    return String(selectedData[fieldConfig.valueField] || '');
  }, [selectedData, fieldConfig.valueField]);

  // Effacer la sélection
  const clearSelection = useCallback(() => {
    setSelectedData(null);
    setDisplayValue('');
  }, []);

  return {
    selectedData,
    displayValue,
    fieldConfig,
    initializeField,
    handleSelect,
    getValue,
    clearSelection
  };
};
